<script setup lang="ts">
import { SongsByLanguageCategory } from "@/models/SongsByLanguageCategory";

const Error = defineAsyncComponent(() => import("@/components/Error.vue"));
const translate = useTranslationStore();
const store = useSongSearchStore();
const appStore = useAppStore();
const { t } = useI18n();
const date = useDateStore();
const route = useRoute();
const songsByLanguage = ref<SongsByLanguageCategory[]>([]);
emitter.on("languageUpdated", store.updateTitleAndDescription);
let mounted = false;

let runningWatch = false;

activatedWatch(
  () => [route.query, translate.currentLanguageCode],
  async () => {
    if (!mounted) {
      return;
    }
    runningWatch = true;
    if (route.query.list === "popular" && betaStore.features.ccliTop100New) {
      return;
    }
    await store.loadSongs();
    store.updateTitleAndDescription();
    runningWatch = false;
  },
);

onActivated(async () => {
  /**
   * Ensures that if the user types the same song number twice, it will redirect to the song page.
   */
  await nextTick();
  if (!runningWatch) {
    if (
      store.responseData &&
      store.responseData.numResults === 1 &&
      store.responseData.page === 1 &&
      store.responseData.items[0].songNumber
    ) {
      router.replace({
        path: `/songs/${store.responseData.items[0].songNumber}/${store.responseData.items[0].slug}`,
        query: {},
      });
    }
  }
});

const betaStore = useBetaStore();

onMounted(async () => {
  if (route.query.list === "popular" && betaStore.features.ccliTop100New) {
    router.push({
      path: "/ccli-top-100",
    });
    return;
  }

  const results = await Promise.all([
    store.resetSearch(),
    await store.getSongsByLanguageData(),
  ]);

  if (!songsByLanguage.value.length) {
    songsByLanguage.value = results[1];
  }

  if (route.query.list?.includes("custom_")) {
    appStore.updateTitle(`${store.title} | ${t("S.PageTabTitle")}`);
  }
  mounted = true;
});

onBeforeUnmount(() => {
  store.abort();
});

const language = computed(() => {
  return Array.isArray(route.query.language)
    ? route.query.language[0]
    : route.query.language;
});

const findSongsByLanguageTitle = computed(() => {
  return language.value
    ? songsByLanguage.value.find(
        (d) => d.language.toLowerCase() === language.value?.toLowerCase(),
      )?.label || null
    : null;
});
const isMassSettingSearch = computed(() => {
  return (route.query.seo as string)?.toLowerCase() === "masssettings";
});

const profileStore = useProfileStore();
const formatter = Intl.NumberFormat(profileStore.profile?.language ?? "en-US");

const search = computed(() => store.queryData.search as string);
</script>

<template>
  <Error v-if="store.status === LoadStatus.Error" />
  <div
    v-else
    :class="{ 'placeholder-glow': store.status === LoadStatus.Loading }"
  >
    <PageHeader
      v-if="store.responseData"
      :img="store.responseData.img || undefined"
      :img-alt="store.responseData.imgAlt || undefined"
      :songs-by-language-title="findSongsByLanguageTitle || undefined"
    >
      <template #h1>
        <span
          v-if="!store.noResults"
          v-html="store.title"
        />
        <span v-else-if="store.noResults">
          {{ $t("S.SearchResultsNoResultsTitle1") }} '{{
            store.queryData.search
          }}'
        </span>
        <span v-else>
          <span
            class="placeholder"
            style="width: 200px"
          />
        </span>
      </template>

      <div>
        <div v-if="!store.noResults">
          <div class="mb-3 mt-0">
            <p
              v-if="store.responseData.copyright"
              class="mb-2 mt-0"
            >
              {{ store.responseData.copyright }}
            </p>
            <p class="m-0">{{ store.responseData.subtitle }}</p>
          </div>
          <div v-html="store.responseData.description || ''" />
          <div
            v-if="store.responseData.rankPublishDisplayDate"
            class="mt-2"
          >
            {{
              $t("SV.LIT_ReleasedOnDate", {
                date: date.formatDate(
                  store.responseData.rankPublishDisplayDate,
                ),
              })
            }}
          </div>
          <LiturgyMassSettingSelect
            v-if="isMassSettingSearch"
            enable-see-all
            class="max-w-md"
          />
        </div>
        <div v-else-if="store.status !== LoadStatus.Ready">
          <span
            class="placeholder"
            style="width: 100%"
          />
        </div>
      </div>

      <ExpandSearchResults
        :model-value="store.queryData.expandToAllSongs"
        @update:model-value="store.toggleExpandOnNoResults()"
      />
    </PageHeader>

    <Container>
      <Card
        v-if="
          store.showCategoryAll &&
          store.filteredVideos.length &&
          store.queryData.page === 1 &&
          store.status === LoadStatus.Ready
        "
        class="mb-4"
      >
        <div>
          <h2 class="mr-3 inline-block text-3xl text-primary">Lyric Videos</h2>
          <span class="ml-3"> {{ store.filteredVideos.length }} results </span>
          <span class="ml-3"></span>
        </div>
        <div class="flex gap-4 overflow-x-auto overflow-y-hidden">
          <BrowseLVItem
            v-for="video in store.filteredVideos"
            :key="video.videoId"
            :data="video"
            class="video-item"
          />
        </div>
        <router-link
          :to="`/lyric-videos/videos?search=${search}`"
          class="uppercase text-primary"
        >
          View All <i class="fa-light fa-chevron-right"
        /></router-link>
      </Card>
      <SearchResults
        v-if="store.responseData"
        :songs="store.items"
        show-rank
        disable-pagination
      >
        <template
          v-if="store.status !== LoadStatus.Ready || !store.noResults"
          #header
        >
          <div
            v-if="!store.noResults"
            class="flex items-center gap-10"
          >
            <div
              v-if="store.showCategoryAll"
              class="shrink-0"
            >
              <h2 class="mr-3 inline-block text-3xl text-primary">Songs</h2>
              <span class="ml-3">
                {{ formatter.format(store.responseData.numResults) }} results
              </span>
            </div>
            <SongFiltersAndSort />
          </div>
          <div v-else-if="store.status !== LoadStatus.Ready">
            <SongFiltersAndSort placeholder />
          </div>
        </template>
        <SongListTable
          v-if="store.status !== LoadStatus.Ready || !store.noResults"
          :songs="store.items"
          :show-rank="store.responseData.showRanking"
          :loading-number="
            (store.status !== LoadStatus.Ready && 10) || undefined
          "
        />
        <div
          v-else
          class="search-tips"
        >
          <h2 class="mb-3 text-2xl text-primary">
            {{ $t("S.SearchResultsNoResultsTitle2") }}
          </h2>
          <ul class="list-disc pl-5">
            <li>{{ $t("S.SearchResultsNoResultsBroaden1") }}</li>
            <li>{{ $t("S.SearchResultsNoResultsBroaden2") }}</li>
            <li>
              {{
                $t("S.SearchResultsNoResultsBroaden3", [
                  $t("SV.MES_SelectBoxExpand"),
                ])
              }}
            </li>
          </ul>
        </div>

        <BasePagination
          v-if="!store.noResults"
          :num-per-page="store.queryData.numPerPage"
          :page="store.queryData.page"
          :count="store.responseData.numResults"
          @update:num-per-page="(e) => store.updateNumPerPage(e)"
          @update:page="(e) => store.updatePage(e)"
        />
      </SearchResults>
    </Container>
  </div>
</template>

<style scoped lang="postcss">
.search-tips {
  @apply px-6 py-5;
}
:deep() {
  .pagination {
    @apply rounded-b-xl;
  }
}
.video-item {
  @apply mr-2 block w-[300px] shrink-0;
}
</style>
